
import { Column, useTable } from 'react-table';
import { classNames } from '@/lib/classNames';
import LoadingSpinner from '@/components/util/LoadingSpinner';

// eslint-disable-next-line @typescript-eslint/ban-types
type Props<T extends object> = {
  columns: readonly Column<T>[];
  data: T[];
  hasMoreData?: boolean;
  loadMore?: () => void;
  onTrClick?: (row: T) => void;
  trSelected?: T | null;
  loading?: boolean;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export function PaginatedTable<T extends object>({
  columns,
  data,
  hasMoreData,
  loadMore,
  onTrClick = undefined,
  trSelected = null,
  loading = false
}: Props<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  return (
    <div className="overflow-hidden rounded-md border bg-white shadow-sm">
      <table {...getTableProps()} className="w-full table-fixed">
        <thead>
          {headerGroups.map((headerGroup) => (
            // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
            <tr
              className="border-b border-gray-300 bg-gray-50"
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column) => (
                // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
                <th
                  className="py-3 px-2 text-left font-bold text-gray-800"
                  {...column.getHeaderProps()}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
              <tr
                {...row.getRowProps()}
                onClick={() => onTrClick?.(row.original)}
                className={classNames(
                  onTrClick &&
                    'cursor-pointer hover:bg-gray-100 focus:bg-gray-100',
                  trSelected === row.original && 'bg-gray-100',
                  'bg-white'
                )}
              >
                {row.cells.map((cell) => {
                  return (
                    // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
                    <td
                      {...cell.getCellProps()}
                      className="border-t border-b border-gray-300 py-2 px-2"
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {loading ? (
            <tr>
              <td
                colSpan={headerGroups
                  .map((x) => x.headers.length)
                  .reduce((x, y) => x + y, 0)}
                className="w-full p-5 text-center"
              >
                <LoadingSpinner />
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {hasMoreData && loadMore ? (
        <div className="flex w-full justify-center py-2 text-center">
          <a
            href="#loadMore"
            onClick={loadMore}
            className="cursor-pointer font-semibold text-secondary-bg"
          >
            Load more
          </a>
        </div>
      ) : null}
    </div>
  );
}
